import { useState, useEffect } from 'react';
import {
  BuildingOffice2Icon,
  PhoneIcon,
  UserGroupIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import {
  getPlatformUsage,
  getAllAgencies,
  UsageAggregate,
} from '../services/api';

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
}

interface AgencyWithUsage {
  id: string;
  name: string;
  slug: string;
  billingPlan: string;
  businessCount: number;
  usage?: UsageAggregate;
}

export default function SuperAdminPage() {
  const { isSuperAdmin } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [agencies, setAgencies] = useState<AgencyWithUsage[]>([]);
  const [platformUsage, setPlatformUsage] = useState<{
    billingPeriod: string;
    totalAgencies: number;
    overallTotals: UsageAggregate['totals'];
    agencyUsages: UsageAggregate[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) return;

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const [usageData, agenciesData] = await Promise.all([
          getPlatformUsage(billingPeriod),
          getAllAgencies(),
        ]);

        setPlatformUsage(usageData);

        // Merge agency data with usage
        const agenciesWithUsage: AgencyWithUsage[] = agenciesData.agencies.map((agency: any) => ({
          ...agency,
          usage: usageData.agencyUsages.find((u) => u.agencyId === agency.id),
        }));

        // Sort by call minutes descending
        agenciesWithUsage.sort((a, b) => {
          const aMinutes = a.usage?.totals.retellMinutes ?? 0;
          const bMinutes = b.usage?.totals.retellMinutes ?? 0;
          return bMinutes - aMinutes;
        });

        setAgencies(agenciesWithUsage);
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [isSuperAdmin, billingPeriod]);

  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900">Access Denied</h3>
        <p className="mt-2 text-sm text-gray-500">
          You don't have permission to view this page.
        </p>
      </div>
    );
  }

  // Generate billing period options (last 12 months)
  const billingPeriodOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    billingPeriodOptions.push({ value, label });
  }

  // Calculate totals using Retell metrics
  const totalMinutes = platformUsage?.overallTotals.retellMinutes ?? 0;
  const totalCalls = platformUsage?.overallTotals.retellCallCount ?? 0;
  const activeAgencies = platformUsage?.agencyUsages.filter((u) =>
    (u.totals.retellMinutes ?? 0) > 0
  ).length ?? 0;

  const stats = platformUsage
    ? [
        {
          name: 'Total Agencies',
          value: platformUsage.totalAgencies,
          icon: BuildingOffice2Icon,
          color: 'bg-blue-500',
        },
        {
          name: 'Active Agencies',
          value: activeAgencies,
          icon: UserGroupIcon,
          color: 'bg-green-500',
        },
        {
          name: 'Total Calls',
          value: totalCalls.toLocaleString(),
          icon: PhoneIcon,
          color: 'bg-purple-500',
        },
        {
          name: 'Total Call Time',
          value: formatDuration(totalMinutes * 60),
          icon: ClockIcon,
          color: 'bg-orange-500',
        },
      ]
    : [];

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Admin</h1>
          <p className="mt-1 text-sm text-gray-500">
            Overview of all agencies and platform usage
          </p>
        </div>
        <div>
          <select
            value={billingPeriod}
            onChange={(e) => setBillingPeriod(e.target.value)}
            className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary-600 sm:text-sm sm:leading-6"
          >
            {billingPeriodOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : error ? (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
            {stats.map((stat) => (
              <div
                key={stat.name}
                className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6 sm:py-6"
              >
                <dt>
                  <div className={`absolute rounded-md ${stat.color} p-3`}>
                    <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  <p className="ml-16 truncate text-sm font-medium text-gray-500">
                    {stat.name}
                  </p>
                </dt>
                <dd className="ml-16 flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </dd>
              </div>
            ))}
          </div>

          {/* Agencies Table */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                All Agencies
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Agency activity sorted by call volume
              </p>
            </div>
            <div className="overflow-x-auto">
              {agencies.length === 0 ? (
                <div className="text-center py-12">
                  <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-semibold text-gray-900">
                    No agencies
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No agencies have been created yet.
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        Agency
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Plan
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                      >
                        Clients
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                      >
                        Calls
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                      >
                        Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {agencies.map((agency) => {
                      const agencyMinutes = agency.usage?.totals.retellMinutes ?? 0;
                      const agencyCalls = agency.usage?.totals.retellCallCount ?? 0;
                      return (
                        <tr key={agency.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="font-medium text-gray-900">{agency.name}</div>
                            <div className="text-gray-500">{agency.slug}</div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                agency.billingPlan === 'enterprise'
                                  ? 'bg-purple-100 text-purple-700'
                                  : agency.billingPlan === 'professional'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {agency.billingPlan}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                            {agency.businessCount || 0}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                            {agencyCalls.toLocaleString()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                            {formatDuration(agencyMinutes * 60)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50">
                      <td
                        colSpan={3}
                        className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        Total
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-right text-sm font-semibold text-gray-900">
                        {totalCalls.toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-right text-sm font-semibold text-gray-900">
                        {formatDuration(totalMinutes * 60)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
